import React from "react";
import { useState, useCallback } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

const data = [
  { day: "Nov 1",  week: "Week 1", value: 3200  },
  { day: "Nov 2",  week: "Week 1", value: 3000  },
  { day: "Nov 3",  week: "Week 1", value: 3500  },
  { day: "Nov 4",  week: "Week 1", value: 3300  },
  { day: "Nov 5",  week: "Week 1", value: 3600  },
  { day: "Nov 6",  week: "Week 1", value: 3400  },
  { day: "Nov 7",  week: "Week 1", value: 3800  },
  { day: "Nov 8",  week: "Week 2", value: 4200  },
  { day: "Nov 9",  week: "Week 2", value: 5800  },
  { day: "Nov 10", week: "Week 2", value: 6400  },
  { day: "Nov 11", week: "Week 2", value: 7100  },
  { day: "Nov 12", week: "Week 2", value: 7600  },
  { day: "Nov 13", week: "Week 2", value: 8200  },
  { day: "Nov 14", week: "Week 2", value: 8800  },
  { day: "Nov 15", week: "Week 2", value: 9400  },
  { day: "Nov 16", week: "Week 2", value: 10100 },
  { day: "Nov 17", week: "Week 2", value: 10800 },
  { day: "Nov 18", week: "Week 2", value: 11400 },
  { day: "Nov 19", week: "Week 2", value: 12100 },
  { day: "Nov 20", week: "Week 2", value: 12800 },
  { day: "Nov 21", week: "Week 2", value: 13200 },
  { day: "Nov 22", week: "Week 2", value: 14100 },
  { day: "Nov 23", week: "Week 2", value: 15600 },
  { day: "Nov 24", week: "Week 2", value: 18400 },
  { day: "Nov 25", week: "Week 2", value: 17800 },
  { day: "Nov 26", week: "Week 2", value: 18600 },
  { day: "Nov 27", week: "Week 2", value: 19200 },
  { day: "Nov 28", week: "Week 2", value: 20100 },
];

interface ChartDataPoint {
  readonly day: string;
  readonly week: string;
  readonly value: number;
}

interface CustomTooltipProps {
  readonly active?: boolean;
  readonly payload?: readonly { readonly payload: ChartDataPoint }[];
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps): React.ReactElement | null => {
  if (!active || !payload || !payload.length) return null;
  const d: ChartDataPoint = payload[0].payload;
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      padding: "6px 12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      textAlign: "center",
      pointerEvents: "none",
    }}>
      <p style={{ margin: 0, fontSize: 11, color: "#6b7280", fontWeight: 500 }}>{d.day}</p>
      <p style={{ margin: "2px 0 0", fontSize: 14, color: "#111827", fontWeight: 700 }}>
        ${d.value.toLocaleString()}
      </p>
    </div>
  );
};

interface CustomDotProps {
  readonly cx?: number;
  readonly cy?: number;
  readonly payload?: ChartDataPoint;
  readonly activeDay?: string | null;
}

const CustomDot = (props: CustomDotProps): React.ReactElement | null => {
  const { cx, cy, payload, activeDay } = props;
  if (payload?.day !== activeDay) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill="#16a34a" stroke="#fff" strokeWidth={2} />
      <circle cx={cx} cy={cy} r={10} fill="#16a34a" fillOpacity={0.15} />
    </g>
  );
};

export default function ChartInterative() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeDay, setActiveDay] = useState<string | null>(null);

  const handleMouseMove = useCallback((e) => {
    if (e && e.activeTooltipIndex !== undefined) {
      setActiveIndex(e.activeTooltipIndex);
      setActiveDay(data[e.activeTooltipIndex]?.day || null);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setActiveIndex(null);
    setActiveDay(null);
  }, []);

  return (
    <div style={{
      padding: "30px",
      marginLeft: "7.5px",
      width: "100%",
      height: "calc(100% - 120px)",
      fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div>
          <p style={{textAlign: "left", margin: 0, fontSize: 12, color: "#9ca3af", fontWeight: 500, letterSpacing: "0.02em" }}>
            Total Revenue
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: "#111827", letterSpacing: "-0.5px" }}>
              $124,500
            </span>
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              background: "#dcfce7",
              color: "#16a34a",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              padding: "3px 8px",
            }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 7L5 3L8 7" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              +14.2%
            </span>
          </div>
        </div>
        <button style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#d1d5db",
          padding: "4px",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          fontSize: 18,
          letterSpacing: 2,
          lineHeight: 1,
        }}>
          ···
        </button>
      </div>

      {/* Chart */}
      <div style={{ marginTop: 12, marginLeft: -4 }}>
        <ResponsiveContainer width="100%" height={130}>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 4, left: 0, bottom: 0 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <defs>
              <linearGradient id="greenGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0.02} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="day"
              ticks={["Nov 1", "Nov 14", "Nov 28"]}
              tickFormatter={(v) => {
                if (v === "Nov 1") return "Week 1";
                if (v === "Nov 14") return "Week 2";
                return "";
              }}
              tick={{ fontSize: 11, fill: "#9ca3af", fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />

            <YAxis hide />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{
                stroke: "#22c55e",
                strokeWidth: 1.5,
                strokeDasharray: "4 3",
              }}
              isAnimationActive={false}
            />

            <Area
              type="monotone"
              dataKey="value"
              stroke="#22c55e"
              strokeWidth={2}
              fill="url(#greenGradient)"
              dot={<CustomDot activeDay={activeDay} />}
              activeDot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}